import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { CheckCircle2, Download, FileText, Plus, Save, UploadCloud, X } from 'lucide-react';
import { createSubmission, getStoredAuth, getSubmission, lookupAuthorByEmail, updateSubmission } from '../lib/auth';

const articleTypes = [
  ['original-research', 'Original Research Article'],
  ['review', 'Review Article'],
  ['systematic-review-meta-analysis', 'Systematic Review and Meta-analysis'],
  ['edge', 'EDGE'],
  ['case-report', 'Case Report/Series'],
  ['others', 'Others'],
];

const STEPS = [
  { id: 1, label: 'Type' },
  { id: 2, label: 'Metadata' },
  { id: 3, label: 'Authors' },
  { id: 4, label: 'Files' },
  { id: 5, label: 'Review' },
  { id: 6, label: 'Submit' },
];

const maxFileSize = 5 * 1024 * 1024;
const allowedTypes = [
  'application/msword',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const emptyAuthor = { firstName: '', lastName: '', email: '', affiliation: '', orcid: '', isCorresponding: false };
const emptyReviewer = { name: '', email: '', affiliation: '' };
const REQUIRED_REVIEWER_COUNT = 3;

function stripHtml(value) {
  const div = document.createElement('div');
  div.innerHTML = value || '';
  return div.textContent || div.innerText || '';
}

function formatSize(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function isStepComplete(step, { form, authors, coverLetter, manuscript, reviewers }) {
  switch (step) {
    case 1:
      return !!form.articleType;
    case 2:
      return !!(form.title.trim() && stripHtml(form.abstract).trim() && form.keywords.length);
    case 3:
      return (
        authors.length > 0 &&
        authors.every((a) => a.firstName && a.lastName && a.email && a.affiliation) &&
        authors.some((a) => a.isCorresponding)
      );
    case 4:
      return !!(
        coverLetter &&
        manuscript &&
        form.officialTemplate &&
        form.coverLetterComplete &&
        form.ethics &&
        form.journalPolicies
      );
    default:
      return false;
  }
}

function authorToFields(author) {
  const names = String(author.name || '').trim().split(/\s+/);
  return {
    firstName: names.slice(0, -1).join(' ') || names[0] || '',
    lastName: names.length > 1 ? names.slice(-1).join(' ') : '',
    email: author.email || '',
    affiliation: author.affiliation || '',
    orcid: author.orcid || '',
    isCorresponding: Boolean(author.isCorresponding),
  };
}

function Submit() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = useMemo(() => getStoredAuth(), []);

  const [showForm, setShowForm] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState('');
  const [draftId, setDraftId] = useState(null);
  const [keywordInput, setKeywordInput] = useState('');
  const [lookupState, setLookupState] = useState({});
  const [errors, setErrors] = useState({});
  const [loadingDraft, setLoadingDraft] = useState(false);

  const [form, setForm] = useState({
    articleType: 'original-research',
    title: '',
    runningTitle: '',
    abstract: '',
    wordCount: '',
    tables: '',
    figures: '',
    keywords: [],
    officialTemplate: false,
    coverLetterComplete: false,
    ethics: false,
    journalPolicies: false,
  });
  const [authors, setAuthors] = useState([
    {
      firstName: auth?.user?.fullName?.split(' ')?.slice(0, -1).join(' ') || auth?.user?.fullName || '',
      lastName: auth?.user?.fullName?.split(' ')?.slice(-1).join(' ') || '',
      email: auth?.user?.email || '',
      affiliation: auth?.user?.affiliation || '',
      orcid: auth?.user?.orcid || '',
      isCorresponding: true,
    },
  ]);
  const [coverLetter, setCoverLetter] = useState(null);
  const [manuscript, setManuscript] = useState(null);
  const [reviewers, setReviewers] = useState([{ ...emptyReviewer }, { ...emptyReviewer }, { ...emptyReviewer }]);

  // Existing file info from saved draft (shown when no new file chosen)
  const [savedCoverLetter, setSavedCoverLetter] = useState(null);
  const [savedManuscript, setSavedManuscript] = useState(null);

  useEffect(() => {
    document.title = 'Submit Manuscript - JAMSD';
  }, []);

  // Load draft from URL param
  useEffect(() => {
    const id = searchParams.get('draft');
    if (!id || !auth?.token) return;

    setLoadingDraft(true);
    setShowForm(true);
    getSubmission(id)
      .then((result) => {
        const sub = result.data;
        if (!sub) return;
        setDraftId(sub._id);

        // Populate authors
        if (sub.authors?.length) {
          setAuthors(sub.authors.map(authorToFields));
        }

        // Populate suggested reviewers
        if (sub.suggestedReviewers?.length) {
          const padded = [...sub.suggestedReviewers.map((r) => ({ name: r.name || '', email: r.email || '', affiliation: r.affiliation || '' }))];
          while (padded.length < REQUIRED_REVIEWER_COUNT) padded.push({ ...emptyReviewer });
          setReviewers(padded);
        }

        // Populate form fields
        setForm({
          articleType: sub.articleType || 'original-research',
          title: sub.title || '',
          runningTitle: '',
                abstract: sub.abstract || '',
          wordCount: '',
          tables: '',
    figures: '',
          keywords: sub.keywords || [],
          officialTemplate: Boolean(sub.declarations?.officialTemplate),
          coverLetterComplete: Boolean(sub.declarations?.coverLetterComplete || sub.declarations?.coverPageComplete),
          ethics: Boolean(sub.declarations?.ethics),
          journalPolicies: Boolean(sub.declarations?.journalPolicies),
        });

        // Store saved file info
        if (sub.coverPageFile?.filename || sub.coverPageFile?.name) {
          setSavedCoverLetter(sub.coverPageFile);
        }
        if (sub.manuscriptFile?.filename || sub.manuscriptFile?.name) {
          setSavedManuscript(sub.manuscriptFile);
        }

        setDraftSaved(`Draft loaded: ${sub.submissionId || 'unsaved draft'}`);
      })
      .catch((err) => setMessage(err.message))
      .finally(() => setLoadingDraft(false));
  }, [searchParams, auth?.token]);

  const stepState = useMemo(() => {
    const effectiveCover = coverLetter || savedCoverLetter;
    const effectiveManuscript = manuscript || savedManuscript;
    const ctx = { form, authors, coverLetter: effectiveCover, manuscript: effectiveManuscript, reviewers };
    return STEPS.reduce((acc, s) => {
      acc[s.id] = isStepComplete(s.id, ctx);
      return acc;
    }, {});
  }, [form, authors, coverLetter, manuscript, savedCoverLetter, savedManuscript, reviewers]);

  const setField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setMessage('');
    setDraftSaved('');
  };

  const addKeyword = (value = keywordInput) => {
    const keyword = value.trim();
    if (!keyword || form.keywords.includes(keyword)) return;
    setForm((prev) => ({ ...prev, keywords: [...prev.keywords, keyword] }));
    setKeywordInput('');
  };

  const removeKeyword = (keyword) => {
    setForm((prev) => ({ ...prev, keywords: prev.keywords.filter((item) => item !== keyword) }));
  };

  const updateAuthor = (index, key, value) => {
    setAuthors((prev) => prev.map((author, idx) => (idx === index ? { ...author, [key]: value } : author)));
  };

  const updateReviewer = (index, key, value) => {
    setReviewers((prev) => prev.map((reviewer, idx) => (idx === index ? { ...reviewer, [key]: value } : reviewer)));
    setErrors((prev) => ({ ...prev, reviewers: undefined }));
    setDraftSaved('');
  };

  const addAuthor = () => setAuthors((prev) => [...prev, { ...emptyAuthor }]);
  const removeAuthor = (index) => setAuthors((prev) => {
    const next = prev.filter((_, idx) => idx !== index);
    if (prev[index]?.isCorresponding && next.length) next[0] = { ...next[0], isCorresponding: true };
    return next;
  });

  const lookupAuthor = async (index) => {
    const email = authors[index]?.email;
    if (!email) return;
    setLookupState((prev) => ({ ...prev, [index]: 'checking' }));
    try {
      const result = await lookupAuthorByEmail(email);
      if (result.found && result.user) {
        const names = String(result.user.fullName || '').split(' ');
        setAuthors((prev) => prev.map((author, idx) => (idx === index ? {
          ...author,
          firstName: names.slice(0, -1).join(' ') || names[0] || author.firstName,
          lastName: names.length > 1 ? names.slice(-1).join(' ') : author.lastName,
          affiliation: result.user.affiliation || author.affiliation,
          orcid: result.user.orcid || author.orcid,
        } : author)));
        setLookupState((prev) => ({ ...prev, [index]: 'filled from user database' }));
      } else {
        setLookupState((prev) => ({ ...prev, [index]: '' }));
      }
    } catch (error) {
      setLookupState((prev) => ({ ...prev, [index]: error.message }));
    }
  };

  const validateFile = (file, label) => {
    if (!file) return `${label} is required.`;
    if (file.size > maxFileSize) return `${label} must be 5 MB or less.`;
    if (!allowedTypes.includes(file.type)) return `${label} must be a DOC, DOCX, or PDF file.`;
    return '';
  };

  const chooseFile = (setter, key, label) => (event) => {
    const file = event.target.files?.[0] || null;
    const error = file ? validateFile(file, label) : '';
    if (!error && file) setter(file);
    if (error) setter(null);
    setErrors((prev) => ({ ...prev, [key]: error || undefined }));
  };

  const buildFormData = (isDraft) => {
    const body = new FormData();
    body.append('articleType', form.articleType);
    body.append('title', form.title);
    body.append('abstract', form.abstract);
    body.append('keywords', JSON.stringify(form.keywords));
    body.append('authors', JSON.stringify(authors.map((author) => ({
      name: `${author.firstName} ${author.lastName}`.trim(),
      email: author.email,
      affiliation: author.affiliation,
      orcid: author.orcid,
      isCorresponding: author.isCorresponding,
    }))));
    body.append('declarations', JSON.stringify({
      authorship: true,
      conflictOfInterest: true,
      officialTemplate: form.officialTemplate,
      coverLetterComplete: form.coverLetterComplete,
      coverPageComplete: form.coverLetterComplete,
      ethics: form.ethics,
      journalPolicies: form.journalPolicies,
    }));
    body.append('suggestedReviewers', JSON.stringify(reviewers.filter((r) => r.name.trim() || r.email.trim() || r.affiliation.trim())));
    if (isDraft) body.append('saveAsDraft', 'true');
    if (coverLetter) body.append('coverLetter', coverLetter);
    if (manuscript) body.append('manuscript', manuscript);
    return body;
  };

  const saveDraft = async () => {
    if (!auth?.token) { navigate('/auth/login'); return; }
    setSavingDraft(true);
    setMessage('');
    setDraftSaved('');
    try {
      let result;
      if (draftId) {
        result = await updateSubmission(draftId, buildFormData(true));
      } else {
        result = await createSubmission(buildFormData(true));
        setDraftId(result.data._id);
        // Update URL without reloading
        const url = new URL(window.location.href);
        url.searchParams.set('draft', result.data._id);
        window.history.replaceState({}, '', url.toString());
      }
      setDraftSaved('Draft saved successfully.');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSavingDraft(false);
    }
  };

  const validateAll = () => {
    const effectiveCover = coverLetter || savedCoverLetter;
    const effectiveManuscript = manuscript || savedManuscript;
    const nextErrors = {};
    if (!form.title.trim()) nextErrors.title = 'Manuscript title is required.';
    if (!stripHtml(form.abstract).trim()) nextErrors.abstract = 'Abstract is required.';
    if (!form.keywords.length) nextErrors.keywords = 'Add at least one keyword.';
    authors.forEach((author, index) => {
      if (!author.firstName || !author.lastName || !author.email || !author.affiliation) {
        nextErrors[`author-${index}`] = 'First name, last name, email, and affiliation are required.';
      }
    });
    if (!authors.some((author) => author.isCorresponding)) nextErrors.authors = 'Select a corresponding author.';
    if (!effectiveCover) nextErrors.coverLetter = 'Cover page is required.';
    if (!effectiveManuscript) nextErrors.manuscript = 'Manuscript is required.';
    if (coverLetter) {
      const e = validateFile(coverLetter, 'Cover page');
      if (e) nextErrors.coverLetter = e;
    }
    if (manuscript) {
      const e = validateFile(manuscript, 'Manuscript');
      if (e) nextErrors.manuscript = e;
    }
    ['officialTemplate', 'coverLetterComplete', 'ethics', 'journalPolicies'].forEach((key) => {
      if (!form[key]) nextErrors[key] = 'Required.';
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goToReview = (event) => {
    event.preventDefault();
    if (!validateAll()) {
      setMessage('Please complete the highlighted fields before reviewing.');
      return;
    }
    setMessage('');
    setActiveStep(5);
  };

  const handleSubmit = async () => {
    if (!auth?.token) { navigate('/auth/login'); return; }
    setSubmitting(true);
    setMessage('');
    try {
      let result;
      if (draftId) {
        // Update the draft and finalize it (no saveAsDraft flag = submit)
        result = await updateSubmission(draftId, buildFormData(false));
      } else {
        result = await createSubmission(buildFormData(false));
      }
      setSubmitted(result.data);
      setActiveStep(6);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!auth?.token) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16">
        <h1 className="font-display text-navy text-3xl font-bold mb-3">Submit Manuscript</h1>
        <p className="font-sans text-slate mb-6">Please sign in before submitting a manuscript.</p>
        <Link to="/auth/login" className="btn-amber px-5 py-3">Sign In</Link>
      </div>
    );
  }

  const downloadLinks = [
    ['/templates/Manuscript_Template.docx', 'Download Manuscript Template'],
    ['/templates/Cover_Letter_Template.docx', 'Download Cover Letter Template'],
    ['/templates/author-guidelines.pdf', 'Download Author Guideline PDF'],
  ];

  const articleTypeLabel = articleTypes.find(([v]) => v === form.articleType)?.[1] || form.articleType;

  if (loadingDraft) {
    return (
      <div className="max-w-3xl mx-auto px-4 pt-28 pb-16 text-center">
        <p className="font-sans text-slate">Loading draft...</p>
      </div>
    );
  }

  return (
    <>
      <section className="bg-white pt-24 pb-5 border-b border-bg-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-sans text-slate text-xs font-semibold uppercase tracking-[0.35em]">Manuscript Submission System</p>
          <h1 className="font-display text-navy text-2xl italic font-bold mt-1">Journal of Applied Medical and Surgical Disciplines — JAMSD</h1>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {!showForm && (
          <section className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-6">
            <div className="bg-white border border-bg-border rounded p-6 shadow-sm">
              <h2 className="font-display text-navy text-2xl font-bold mb-4">Submission Guidelines</h2>
              <p className="font-sans text-sm text-gray-700 leading-relaxed mb-4">
                Use the official JAMSD templates only. Authors should prepare the cover page and manuscript offline, then complete this online form.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {['Prepare Cover Page and Manuscript files.', 'Add all authors, affiliations, and corresponding author.', 'Include abstract, keywords, declarations, and ethics approval number.', 'Upload DOC, DOCX, or PDF files up to 5 MB each.'].map((item) => (
                  <div key={item} className="flex gap-3 rounded border border-bg-border bg-bg-ice p-3 text-sm">
                    <CheckCircle2 className="h-5 w-5 flex-none text-amber" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => setShowForm(true)} className="btn-amber px-5 py-3">Continue to Submission Form</button>
            </div>
            <div className="bg-white border border-bg-border rounded p-6 shadow-sm">
              <h2 className="font-display text-navy text-xl font-bold mb-4">Downloads</h2>
              <div className="space-y-3">
                {downloadLinks.map(([href, label]) => (
                  <a key={href} href={href} download className="flex items-center gap-3 rounded border border-bg-border p-3 hover:border-amber">
                    <Download className="h-5 w-5 text-amber" />
                    <span className="font-sans text-sm font-semibold text-navy">{label}</span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {showForm && (
          <div className="space-y-4">
            {/* Step indicator */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-1 rounded border border-bg-border bg-white p-1">
              {STEPS.map((step) => {
                const done = stepState[step.id];
                const active = activeStep === step.id;
                const past = step.id < activeStep;
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setActiveStep(step.id)}
                    className={`flex items-center justify-center gap-1 rounded px-3 py-2 text-xs font-sans font-semibold transition-colors ${
                      active
                        ? 'bg-navy text-white'
                        : done && past
                        ? 'bg-green-50 text-green-700'
                        : 'bg-white text-slate'
                    }`}
                  >
                    {done && past ? (
                      <CheckCircle2 className="h-3.5 w-3.5 flex-none" />
                    ) : (
                      <span className="flex-none">{step.id}</span>
                    )}
                    {step.label}
                  </button>
                );
              })}
            </div>

            {/* Draft status bar */}
            {(draftId || draftSaved) && (
              <div className={`rounded border px-4 py-2 text-sm flex items-center gap-2 ${draftSaved.startsWith('Draft saved') || draftSaved.startsWith('Draft loaded') ? 'border-green-200 bg-green-50 text-green-700' : 'border-bg-border bg-bg-ice text-slate'}`}>
                <Save className="h-4 w-4 flex-none" />
                {draftSaved || (draftId ? 'Draft in progress' : '')}
              </div>
            )}

            {message && <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{message}</div>}

            {/* Submitted confirmation */}
            {submitted ? (
              <section className="bg-white border border-bg-border rounded p-8 text-center shadow-sm">
                <CheckCircle2 className="mx-auto h-12 w-12 text-amber mb-4" />
                <h2 className="font-display text-navy text-3xl font-bold">Thank You</h2>
                <p className="font-sans text-slate mt-3">Manuscript received successfully. Confirmation email will be sent automatically.</p>
                <div className="mt-6 inline-block rounded border border-bg-border bg-bg-ice px-6 py-4">
                  <p className="font-sans text-xs uppercase text-slate">Submission ID</p>
                  <p className="font-display text-3xl font-bold text-navy">{submitted.submissionId}</p>
                </div>
                <div className="mt-6"><Link to="/dashboard" className="btn-amber px-5 py-3">Track My Submission</Link></div>
              </section>
            ) : activeStep === 5 ? (
              /* Review step */
              <div className="space-y-4">
                <Panel title="Review Your Submission">
                  <div className="space-y-4 text-sm">
                    <ReviewRow label="Article Type" value={articleTypeLabel} />
                    <ReviewRow label="Title" value={form.title} />
                    {form.runningTitle && <ReviewRow label="Running Title" value={form.runningTitle} />}
                    <div>
                      <p className="text-xs uppercase text-slate font-semibold mb-1">Abstract</p>
                      <div className="prose prose-sm max-w-none border border-bg-border rounded p-3 bg-bg-ice" dangerouslySetInnerHTML={{ __html: form.abstract }} />
                    </div>
                    <ReviewRow label="Keywords" value={form.keywords.join(', ')} />
                  </div>
                </Panel>

                <Panel title="Authors">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {authors.map((a, i) => (
                      <div key={i} className="rounded border border-bg-border bg-bg-ice p-3 text-sm">
                        <p className="font-semibold text-navy">
                          {a.firstName} {a.lastName}
                          {a.isCorresponding && <span className="ml-2 text-xs rounded-full bg-navy text-white px-2 py-0.5">Corresponding</span>}
                        </p>
                        <p className="text-slate">{a.email}</p>
                        <p className="text-gray-500 text-xs">{a.affiliation}</p>
                        {a.orcid && <p className="text-gray-400 text-xs">ORCID: {a.orcid}</p>}
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel title="Files & Declarations">
                  <div className="space-y-2 text-sm">
                    <ReviewRow
                      label="Cover Page"
                      value={coverLetter ? `${coverLetter.name} (${formatSize(coverLetter.size)})` : savedCoverLetter ? `${savedCoverLetter.filename || savedCoverLetter.name} (previously uploaded)` : '—'}
                    />
                    <ReviewRow
                      label="Manuscript"
                      value={manuscript ? `${manuscript.name} (${formatSize(manuscript.size)})` : savedManuscript ? `${savedManuscript.filename || savedManuscript.name} (previously uploaded)` : '—'}
                    />
                    <div className="mt-3 space-y-1">
                      {[
                        ['officialTemplate', 'Manuscript prepared using official template'],
                        ['coverLetterComplete', 'Cover page contains all author names and institutional details'],
                        ['ethics', 'Ethical approval obtained'],
                        ['journalPolicies', 'References formatted and journal policies accepted'],
                      ].map(([key, label]) => (
                        <p key={key} className={`flex items-center gap-2 text-xs ${form[key] ? 'text-green-700' : 'text-red-600'}`}>
                          <CheckCircle2 className="h-3.5 w-3.5 flex-none" />
                          {label}
                        </p>
                      ))}
                    </div>
                  </div>
                </Panel>

                <Panel title="Suggested Peer Reviewers">
                  <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {reviewers.map((reviewer, i) => (
                        <div key={i} className="rounded border border-bg-border bg-bg-ice p-3 text-sm">
                          <p className="font-semibold text-navy">{reviewer.name || '—'}</p>
                          <p className="text-slate">{reviewer.email}</p>
                          <p className="text-gray-500 text-xs">{reviewer.affiliation}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Panel>

                <div className="flex justify-between rounded bg-white border border-bg-border p-4">
                  <button type="button" onClick={() => setActiveStep(4)} className="rounded border border-bg-border px-4 py-2 font-sans text-sm">← Back to Edit</button>
                  <button
                    type="button"
                    disabled={submitting}
                    onClick={handleSubmit}
                    className="btn-amber px-5 py-2"
                  >
                    {submitting ? 'Submitting...' : 'Submit Manuscript →'}
                  </button>
                </div>
              </div>
            ) : (
              /* Edit steps 1–4 */
              <form onSubmit={goToReview} className="space-y-4">
                <Panel title="Article Metadata">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input label="Manuscript Title" name="title" value={form.title} onChange={setField} required error={errors.title} className="md:col-span-2" />
                    <Input label="Running Title" name="runningTitle" value={form.runningTitle} onChange={setField} />
                    <label className="font-sans text-xs font-semibold text-navy">Article Type *
                      <select name="articleType" value={form.articleType} onChange={setField} className="mt-1 w-full rounded border border-bg-border px-3 py-2">
                        {articleTypes.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                      </select>
                    </label>
                    <Input label="Word Count" name="wordCount" value={form.wordCount} onChange={setField} />
                    <Input label="Number of Tables" name="tables" value={form.tables} onChange={setField} />
                    <Input label="Number of Figures" name="figures" value={form.figures} onChange={setField} />
                    <div className="md:col-span-2">
                      <p className="font-sans text-xs font-semibold text-navy mb-1">Abstract *</p>
                      <ReactQuill theme="snow" value={form.abstract} onChange={(value) => { setForm((prev) => ({ ...prev, abstract: value })); setDraftSaved(''); }} className="bg-white" />
                      {errors.abstract && <span className="text-xs text-red-600">{errors.abstract}</span>}
                    </div>
                    <div className="md:col-span-2">
                      <p className="font-sans text-xs font-semibold text-navy mb-1">Keywords *</p>
                      <input
                        value={keywordInput}
                        onChange={(event) => setKeywordInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ',') {
                            event.preventDefault();
                            addKeyword();
                          }
                        }}
                        placeholder="Type keyword and press Enter"
                        className="w-full rounded border border-bg-border px-3 py-2"
                      />
                      <div className="flex flex-wrap gap-2 mt-2">
                        {form.keywords.map((keyword) => (
                          <button key={keyword} type="button" onClick={() => removeKeyword(keyword)} className="inline-flex items-center gap-1 rounded-full bg-bg-tag px-3 py-1 text-xs font-sans text-navy">
                            {keyword}<X className="h-3 w-3" />
                          </button>
                        ))}
                      </div>
                      {errors.keywords && <span className="text-xs text-red-600">{errors.keywords}</span>}
                    </div>
                  </div>
                </Panel>

                <Panel title="Authors & Affiliations">
                  <div className="space-y-3">
                    {authors.map((author, index) => (
                      <div key={index} className="rounded border border-bg-border bg-bg-ice p-4">
                        <div className="flex items-center justify-between mb-3">
                          <p className="font-sans text-xs font-semibold text-slate">Author {index + 1}</p>
                          <div className="flex items-center gap-2">
                            {author.isCorresponding && <span className="rounded-full bg-navy px-2 py-0.5 text-xs text-white">Corresponding</span>}
                            {authors.length > 1 && <button type="button" onClick={() => removeAuthor(index)} className="text-xs text-red-600">Remove</button>}
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input label="First Name" value={author.firstName} onChange={(event) => updateAuthor(index, 'firstName', event.target.value)} required />
                          <Input label="Last Name" value={author.lastName} onChange={(event) => updateAuthor(index, 'lastName', event.target.value)} required />
                          <label className="font-sans text-xs font-semibold text-navy">Email *
                            <input value={author.email} onChange={(event) => updateAuthor(index, 'email', event.target.value)} onBlur={() => lookupAuthor(index)} className="mt-1 w-full rounded border border-bg-border px-3 py-2" />
                            {lookupState[index] && <span className="block mt-1 text-xs text-slate">{lookupState[index]}</span>}
                          </label>
                          <Input label="ORCID ID" value={author.orcid} onChange={(event) => updateAuthor(index, 'orcid', event.target.value)} />
                          <Input label="Affiliation" value={author.affiliation} onChange={(event) => updateAuthor(index, 'affiliation', event.target.value)} required className="md:col-span-2" />
                          <label className="flex items-center gap-2 font-sans text-xs text-navy">
                            <input type="radio" checked={author.isCorresponding} onChange={() => setAuthors((prev) => prev.map((item, idx) => ({ ...item, isCorresponding: idx === index })))} />
                            Mark as corresponding author
                          </label>
                        </div>
                        {errors[`author-${index}`] && <p className="text-xs text-red-600 mt-2">{errors[`author-${index}`]}</p>}
                      </div>
                    ))}
                    {errors.authors && <p className="text-xs text-red-600">{errors.authors}</p>}
                    <button type="button" onClick={addAuthor} className="w-full rounded border border-dashed border-slate px-4 py-2 font-sans text-sm font-semibold text-navy flex items-center justify-center gap-2">
                      <Plus className="h-4 w-4" /> Add another author
                    </button>
                  </div>
                </Panel>

                <Panel title="File Uploads">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FileBox
                      label="Upload Cover Page"
                      file={coverLetter}
                      savedFile={savedCoverLetter}
                      error={errors.coverLetter}
                      onChange={chooseFile(setCoverLetter, 'coverLetter', 'Cover page')}
                    />
                    <FileBox
                      label="Upload Manuscript"
                      file={manuscript}
                      savedFile={savedManuscript}
                      error={errors.manuscript}
                      onChange={chooseFile(setManuscript, 'manuscript', 'Manuscript')}
                    />
                  </div>
                  <p className="mt-3 font-sans text-sm font-semibold text-navy">Plagiarism/Similarity Index less than 15%.</p>
                  <div className="mt-4 space-y-2 font-sans text-sm text-gray-700">
                    {[
                      ['officialTemplate', 'Manuscript is prepared using the official template'],
                      ['coverLetterComplete', 'Cover page contains all author names and institutional details'],
                      ['ethics', 'Ethical approval was taken from the committee for animal/human studies'],
                      ['journalPolicies', 'References formatted and journal policies accepted'],
                    ].map(([name, label]) => (
                      <label key={name} className="flex items-center gap-2">
                        <input type="checkbox" name={name} checked={form[name]} onChange={setField} />
                        {label}
                        {errors[name] && <span className="text-xs text-red-600">{errors[name]}</span>}
                      </label>
                    ))}
                  </div>
                </Panel>

                <Panel title="Suggested Peer Reviewers">
                  <p className="font-sans text-xs font-semibold text-navy mb-1">Suggested Peer Reviewers (optional)</p>
                  <p className="font-sans text-xs text-slate mb-3">You may suggest up to 3 potential peer reviewers with their email and affiliation.</p>
                  <div className="space-y-3">
                    {reviewers.map((reviewer, index) => (
                      <div key={index} className="rounded border border-bg-border bg-bg-ice p-4">
                        <p className="font-sans text-xs font-semibold text-slate mb-3">Reviewer {index + 1}</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <Input label="Name" value={reviewer.name} onChange={(event) => updateReviewer(index, 'name', event.target.value)} />
                          <Input label="Email" type="email" value={reviewer.email} onChange={(event) => updateReviewer(index, 'email', event.target.value)} />
                          <Input label="Affiliation" value={reviewer.affiliation} onChange={(event) => updateReviewer(index, 'affiliation', event.target.value)} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {errors.reviewers && <p className="text-xs text-red-600 mt-2">{errors.reviewers}</p>}
                </Panel>

                <div className="flex justify-between rounded bg-white border border-bg-border p-4">
                  <button
                    type="button"
                    disabled={savingDraft}
                    onClick={saveDraft}
                    className="inline-flex items-center gap-2 rounded border border-bg-border px-4 py-2 font-sans text-sm hover:border-amber disabled:opacity-60"
                  >
                    <Save className="h-4 w-4" />
                    {savingDraft ? 'Saving...' : 'Save Draft'}
                  </button>
                  <button type="submit" className="btn-amber px-5 py-2">Continue to Review →</button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-2 border-b border-bg-border pb-2 last:border-0">
      <p className="text-xs uppercase text-slate font-semibold">{label}</p>
      <p className="text-navy">{value || '—'}</p>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="rounded border border-bg-border bg-white shadow-sm overflow-hidden">
      <div className="bg-bg-ice border-b border-bg-border px-4 py-2">
        <h2 className="font-sans text-xs font-bold uppercase tracking-wide text-slate">{title}</h2>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Input({ label, error, required, className = '', ...props }) {
  return (
    <label className={`font-sans text-xs font-semibold text-navy ${className}`}>
      {label}{required ? ' *' : ''}
      <input {...props} className="mt-1 w-full rounded border border-bg-border px-3 py-2" />
      {error && <span className="block mt-1 text-xs text-red-600">{error}</span>}
    </label>
  );
}

function FileBox({ label, file, savedFile, error, onChange }) {
  const displayFile = file || null;
  const hasSaved = !file && savedFile;
  return (
    <label className="block rounded border border-dashed border-bg-border bg-bg-ice p-5 text-center font-sans text-sm text-slate">
      <UploadCloud className="h-7 w-7 text-amber mx-auto mb-2" />
      {label}
      <input type="file" accept=".doc,.docx,.pdf" onChange={onChange} className="mt-3 w-full rounded border border-bg-border bg-white px-3 py-2 text-left" />
      <span className="block mt-2 text-xs">Accepted: .doc, .docx, .pdf · Max 5 MB</span>
      {hasSaved && (
        <span className="mt-2 flex items-center justify-center gap-2 text-xs text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {savedFile.filename || savedFile.name} (saved)
        </span>
      )}
      {displayFile && (
        <span className="mt-3 flex items-center justify-center gap-2 text-xs text-navy">
          <FileText className="h-4 w-4" />{displayFile.name} ({formatSize(displayFile.size)})
        </span>
      )}
      {error && <span className="block mt-2 text-xs text-red-600">{error}</span>}
    </label>
  );
}

export default Submit;
